// src/components/forms/paymentForm.ts

import {
	createElement,
	getElementByIdOrThrow,
	getElementByIdSafe,
	enableButton,
	disableButton,
} from "../../utils/domHelpers";
import { createProductCard } from "../cards/productCard";
import { createFooterButton } from "../common/buttons";
import { Product, SavedPaymentMethod, SquarePayments } from "../../types/api";
import { PaymentMethodType } from "../../types/domain";
import { messageToast } from "../common/toastMessage";
import bootstrap from "../../utils/bootstrap";
import {
	SQUARE_APPLICATION_ID,
	SQUARE_LOCATION_ID,
	SQUARE_ENVIRONMENT,
} from "../../utils/config"; // Import Square config

// --- Global Variables for Square ---
// TODO: Initialize these properly, perhaps in a dedicated service or on page load
declare var Square: any; // Use 'any' if @types/square-web-payments-sdk is not installed
let payments: SquarePayments | null = null;
let card: any = null; // Store the card instance: Use 'any' or Square's Card type
let applePayInstance: any = null; // Store Apple Pay instance
let googlePayInstance: any = null; // Store Google Pay instance

/**
 * Loads the Square Web Payments SDK script if it's not already loaded
 * @returns Promise that resolves when the script is loaded
 */
function loadSquareScript(): Promise<void> {
	return new Promise((resolve, reject) => {
		// Check if script is already loaded
		if (typeof Square !== "undefined") {
			return resolve();
		}

		const script = document.createElement("script");
		// Use the environment to determine the correct URL
		const environment = SQUARE_ENVIRONMENT === "sandbox" ? "sandbox." : "";
		script.src = `https://${environment}web.squarecdn.com/v1/square.js`;
		script.type = "text/javascript";
		script.async = true;
		script.onload = () => {
			resolve();
		};
		script.onerror = (error) => {
			console.error("Failed to load Square SDK script:", error);
			reject(new Error("Failed to load Square SDK script"));
		};
		document.head.appendChild(script);
	});
}

/**
 * Initializes the Square Web Payments SDK.
 * Should be called once when the payment page/component loads.
 */
export async function initializeSquarePaymentsSDK(): Promise<void> {
	if (payments) return; // Already initialized

	if (typeof Square === "undefined") {
		await loadSquareScript();
	}

	try {
		payments = Square.payments(SQUARE_APPLICATION_ID, SQUARE_LOCATION_ID);
	} catch (error) {
		console.error("Failed to initialize Square Payments SDK:", error);
		payments = null; // Ensure it's null on failure
	}
}

/**
 * Renders the product selection step of the purchase flow.
 * @param containerId - ID of the container element.
 * @param products - Array of available products.
 * @param onProductSelect - Callback function when a product is selected and 'Next' is clicked.
 * @param darkText - Optional flag for dark text theme.
 */
export function renderProductSelection(
	containerId: string,
	products: Product[],
	onProductSelect: (productId: string, productPrice: string) => void,
	darkText: boolean = false,
): void {
	const container = getElementByIdOrThrow(containerId);
	container.innerHTML = ""; // Clear previous content

	const form = createElement("form", { id: "purchase-form" }); // Form not strictly needed if just selecting
	const productListDiv = createElement(
		"div",
		{
			id: "product-list",
			className: "container p-2", // Classes from original
		},
		form,
	);

	// Add header
	const header = createElement("h4", {
		className: `py-2 fw-bold ${
			darkText ? "text-center" : "text-white text-center"
		}`,
		innerHTML: "Select your product", // Using innerHTML directly for simplicity
	});
	productListDiv.appendChild(header);

	// Create product cards
	products.forEach((product) => {
		createProductCard(productListDiv, product, "product"); // Use 'product' as group name
	});

	// Add footer with Next button
	const footer = createFooterButton(form, {
		buttonId: "submitButton", // Consistent ID
		buttonText: "Next",
		disabled: true, // Initially disabled
	});

	container.appendChild(form);

	// Add event listener to the form (or button)
	footer.querySelector("#submitButton")?.addEventListener("click", (event) => {
		event.preventDefault();
		const selectedProductInput = form.querySelector<HTMLInputElement>(
			'input[name="product"]:checked',
		);

		if (!selectedProductInput) {
			messageToast({
				message: "Please select a product before continuing",
				background: "text-bg-danger",
			});
			return;
		}

		const productId = selectedProductInput.value;
		const productPrice = selectedProductInput.getAttribute("data-price");

		if (!productPrice) {
			console.error("Selected product card is missing data-price attribute.");
			messageToast({
				message: "Error selecting product. Please try again.",
				background: "text-bg-danger",
			});
			return;
		}

		// Store selection in session storage (as done in original JS)
		sessionStorage.setItem("product-purchase", productId);
		sessionStorage.setItem("product_price", productPrice);

		onProductSelect(productId, productPrice); // Call callback to proceed
	});
}

// --- Payment Method Rendering ---

// --- Payment Processing Overlay ---

/**
 * Shows a full-screen spinner overlay while payment is being processed
 */
function showPaymentProcessingOverlay(): HTMLElement {
	const existing = document.getElementById("payment-processing-overlay");
	if (existing) return existing;

	const overlay = document.createElement("div");
	overlay.id = "payment-processing-overlay";
	overlay.style.cssText = [
		"position: fixed",
		"top: 0",
		"left: 0",
		"width: 100%",
		"height: 100%",
		"background-color: rgba(0,0,0,0.65)",
		"display: flex",
		"flex-direction: column",
		"align-items: center",
		"justify-content: center",
		"z-index: 9999",
	].join(";");
	overlay.innerHTML = `
		<div class="spinner-border text-light" role="status" style="width: 3rem; height: 3rem;">
			<span class="visually-hidden">Processing...</span>
		</div>
		<p class="text-white mt-3 fw-semibold">Processing your payment, please wait...</p>
	`;
	document.body.appendChild(overlay);
	return overlay;
}

/**
 * Removes the payment processing overlay
 */
function hidePaymentProcessingOverlay(): void {
	document.getElementById("payment-processing-overlay")?.remove();
}

/**
 * Renders the saved card selection form.
 * @param container - The parent element to render into.
 * @param paymentMethods - Array of saved payment methods.
 * @param onPayWithSavedCard - Callback when 'Pay' is clicked for a saved card.
 * @param onAddNewCard - Callback when 'Add New Card' is clicked.
 * @param darkText - Optional flag for dark text theme.
 */
function renderSavedCardForm(
	container: HTMLElement,
	paymentMethods: SavedPaymentMethod[],
	onPayWithSavedCard: (methodId: string) => Promise<void>,
	onAddNewCard: () => void,
	darkText: boolean,
): void {
	const form = createElement("form", { id: "payment-form" }); // Form needed for submission logic

	const headerTextClass = darkText ? "text-center" : "text-white text-center";
	const header = createElement("h4", {
		className: `py-2 fw-bold ${headerTextClass}`,
		textContent: "Payment Details",
	});
	form.appendChild(header);

	const savedCardsHeaderDiv = createElement("div", { className: "mb-3" });
	createElement(
		"h5",
		{
			textContent: "Saved Cards",
			style: { color: darkText ? "inherit" : "white" },
		},
		savedCardsHeaderDiv,
	);
	form.appendChild(savedCardsHeaderDiv);

	const savedCardsList = createElement("div", {
		id: "saved-cards-list",
		className: "mb-3",
	});
	paymentMethods.forEach((method, index) => {
		createSavedCardDisplay(savedCardsList, method, index === 0); // Pre-select the first card
	});
	form.appendChild(savedCardsList);

	// --- Footer Buttons ---
	const footer = createElement("div", { id: "footerSection" }); // Wrapper for buttons
	const buttonFooter = createFooterButton(footer, {
		buttonId: "card-button", // ID for the pay button
		buttonText: `Pay with saved card (AUD$${
			sessionStorage.getItem("product_price") || "0.00"
		})`,
		disabled: false, // Enable initially if a card is pre-selected
	});

	// Associate the submit button with the form explicitly
	const payButton = buttonFooter.querySelector(
		"#card-button",
	) as HTMLButtonElement;
	if (payButton) {
		payButton.setAttribute("form", "payment-form");
	}

	// Update button container to stack vertically
	const buttonContainer = buttonFooter.querySelector("#button-container");
	if (buttonContainer) {
		buttonContainer.classList.remove("justify-content-around");
		buttonContainer.classList.add("flex-column");
	}

	// Add New Card Button (prepended)
	const addNewCardButton = createElement("button", {
		className: "btn btn-dark w-100 btn-black mb-2", // Style from original
		innerHTML: '<i class="fa-solid fa-square-plus"></i> Add New Card',
		attributes: { type: "button" }, // Important: type="button" to prevent form submission
	});
	addNewCardButton.addEventListener("click", onAddNewCard);
	buttonContainer?.prepend(addNewCardButton); // Add before the pay button

	form.appendChild(footer);
	container.appendChild(form);

	// Add submit listener to the form (triggered by the pay button)
	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		const selectedCardInput = form.querySelector<HTMLInputElement>(
			'input[name="savedCard"]:checked',
		);
		if (!selectedCardInput) {
			messageToast({
				message: "Please select a card before continuing",
				background: "text-bg-danger",
			});
			return;
		}
		disableButton("card-button"); // Disable on submit
		showPaymentProcessingOverlay();
		try {
			await onPayWithSavedCard(selectedCardInput.value); // Pass the provider_id
		} finally {
			hidePaymentProcessingOverlay();
		}
	});
}

/**
 * Creates the display element for a single saved card.
 * @param parent - The parent element (e.g., savedCardsList).
 * @param method - The saved payment method data.
 * @param isSelected - Whether this card should be pre-selected.
 */
function createSavedCardDisplay(
	parent: HTMLElement,
	method: SavedPaymentMethod,
	isSelected: boolean,
): void {
	const card = createElement("div", {
		className: `card mb-2 bg-light ${isSelected ? "cardSelected" : ""}`, // Add selected class if needed
		style: { cursor: "pointer" },
	});

	const cardBody = createElement("div", { className: "card-body" }, card);

	// Hidden radio input
	const radioInput = createElement(
		"input",
		{
			className: "card-checkbox",
			attributes: {
				type: "radio",
				name: "savedCard",
				value: method.provider_id, // Use Square's ID as the value
				required: "true",
				hidden: "true",
				...(isSelected && { checked: "true" }), // Set checked attribute if pre-selected
			},
		},
		cardBody,
	);

	const row = createElement(
		"div",
		{ className: "row align-items-center" },
		cardBody,
	); // Align items vertically

	// Card Logo Column
	const logoCol = createElement("div", { className: "col-2" }, row);
	let cardLogo = "/static/img/generic.png"; // Default logo
	const brand = method.card_brand.toLowerCase();
	if (brand === "visa") cardLogo = "/static/img/visa.png";
	else if (brand === "mastercard") cardLogo = "/static/img/mastercard.png";
	else if (brand === "american_express") cardLogo = "/static/img/amex.png";
	createElement(
		"img",
		{
			className: "card-logo w-100 h-auto", // Responsive logo
			attributes: { src: cardLogo, alt: method.card_brand },
		},
		logoCol,
	);

	// Card Details Column
	const detailsCol = createElement("div", { className: "col-8" }, row);
	createElement(
		"p",
		{
			className: "card-text fw-bold mb-1 text-capitalize text-dark", // Added text-dark for black text
			textContent: brand,
		},
		detailsCol,
	);
	createElement(
		"p",
		{
			className: "card-text mb-1 text-dark", // Added text-dark for black text
			textContent: `Ending in ${method.last_4}`,
		},
		detailsCol,
	);
	createElement(
		"p",
		{
			className: "card-text text-dark mb-0", // Remove bottom margin
			textContent: `Exp. date ${String(method.expire_month).padStart(2, "0")}/${
				method.expire_year
			}`, // Pad month
		},
		detailsCol,
	);

	// Delete Icon Column
	const deleteCol = createElement("div", { className: "col-2 text-end" }, row); // Align icon right
	const deleteIcon = createElement("i", {
		className: "fa-solid fa-trash text-danger", // Make icon red for delete action
		style: { cursor: "pointer", fontSize: "1.1rem" },
	});
	deleteIcon.addEventListener("click", async (event) => {
		event.stopPropagation(); // Prevent card selection when clicking delete icon

		// Create Bootstrap offcanvas confirmation dialog
		const offcanvasDiv = createElement("div", {
			className: "offcanvas offcanvas-bottom h-auto",
			attributes: {
				tabindex: "-1",
				id: "confirmDeleteOffcanvas",
				"aria-labelledby": "confirmDeleteOffcanvasLabel",
			},
		});

		const offcanvasHeader = createElement(
			"div",
			{ className: "offcanvas-header" },
			offcanvasDiv,
		);

		createElement(
			"h5",
			{
				id: "confirmDeleteOffcanvasLabel",
				className: "offcanvas-title text-dark",
				textContent: "Delete Card",
			},
			offcanvasHeader,
		);

		createElement(
			"button",
			{
				className: "btn-close",
				attributes: {
					type: "button",
					"data-bs-dismiss": "offcanvas",
					"aria-label": "Close",
				},
			},
			offcanvasHeader,
		);

		const offcanvasBody = createElement(
			"div",
			{ className: "offcanvas-body" },
			offcanvasDiv,
		);

		createElement(
			"p",
			{
				className: "text-dark",
				textContent: `Are you sure you want to delete your ${method.card_brand} card ending in ${method.last_4}?`,
			},
			offcanvasBody,
		);

		const buttonContainer = createElement(
			"div",
			{ className: "d-flex gap-2" },
			offcanvasBody,
		);

		const confirmButton = createElement(
			"button",
			{
				className: "btn btn-danger flex-grow-1",
				textContent: "Delete",
			},
			buttonContainer,
		);

		const cancelButton = createElement(
			"button",
			{
				className: "btn btn-secondary flex-grow-1",
				textContent: "Cancel",
				attributes: { "data-bs-dismiss": "offcanvas" },
			},
			buttonContainer,
		);

		document.body.appendChild(offcanvasDiv);

		// Initialize and show the offcanvas
		const offcanvasInstance = new bootstrap.Offcanvas(offcanvasDiv);
		offcanvasInstance.show();

		// Handle delete confirmation
		confirmButton.addEventListener("click", async () => {
			offcanvasInstance.hide();

			try {
				// Import and call the delete API
				const { deleteUserPaymentMethod } = await import("../../api/payments");
				const result = await deleteUserPaymentMethod(method.id);

				if (result) {
					card.remove();
					messageToast({
						message: "Card deleted successfully",
						background: "text-bg-success",
					});

					// If no cards remain, we might want to refresh the payment form
					const remainingCards = parent.querySelectorAll(".card");
					if (remainingCards.length === 0) {
						messageToast({
							message: "No saved cards remaining. Please add a new card.",
							background: "text-bg-info",
						});
					}
				} else {
					messageToast({
						message: "Failed to delete card",
						background: "text-bg-danger",
					});
				}
			} catch (error) {
				console.error("Error deleting card:", error);
				messageToast({
					message: "An error occurred while deleting the card",
					background: "text-bg-danger",
				});
			}
		});

		// Clean up offcanvas element after it's hidden
		offcanvasDiv.addEventListener(
			"hidden.bs.offcanvas",
			() => {
				offcanvasDiv.remove();
			},
			{ once: true },
		);
	});
	deleteCol.appendChild(deleteIcon);

	// Card click listener
	card.addEventListener("click", () => {
		if (radioInput.checked) return; // Don't re-process if already checked

		radioInput.checked = true;
		// Update styles for all cards in the list
		const allCards = parent.querySelectorAll<HTMLElement>(".card");
		allCards.forEach((otherCard) => {
			otherCard.classList.toggle("cardSelected", otherCard === card);
			const otherRadio = otherCard.querySelector<HTMLInputElement>(
				'input[name="savedCard"]',
			);
			if (otherRadio && otherRadio !== radioInput) {
				otherRadio.checked = false;
			}
		});
		enableButton("card-button"); // Ensure pay button is enabled
	});

	parent.appendChild(card);
}

/**
 * Renders the form for adding a new payment method (Card, Apple Pay, Google Pay).
 * @param container - The parent element to render into.
 * @param onPayWithNewMethod - Callback when payment is submitted via Card/Apple/Google Pay.
 * @param darkText - Optional flag for dark text theme.
 */
async function renderNewPaymentMethodForm(
	container: HTMLElement,
	onPayWithNewMethod: (
		methodType: PaymentMethodType,
		paymentMethodInstance: any,
		billingData?: any,
		storeCard?: boolean,
	) => Promise<void>,
	darkText: boolean,
): Promise<void> {
	const wrapperDiv = createElement("div", {
		style: { padding: "15px", color: darkText ? "black" : "white" },
	});

	// --- Wallet Buttons (Apple/Google Pay) ---
	const applePayButton = createElement("div", { id: "apple-pay-button" });
	const googlePayButton = createElement("div", {
		id: "google-pay-button",
		className: "w-100 pb-3 pt-3",
	}); // Classes from original

	// Append wallet buttons first (visibility handled later)
	wrapperDiv.appendChild(applePayButton);
	wrapperDiv.appendChild(googlePayButton);

	// --- "OR" Separator ---
	const orDiv = createElement("div", {
		id: "or-div",
		className: "col-12 d-flex align-items-center my-3",
	}); // Added margin
	const hrStyle = {
		flex: "1",
		height: "1px",
		backgroundColor: darkText ? "#ccc" : "#fff",
	}; // Adjusted color based on theme
	createElement("hr", { style: hrStyle }, orDiv);
	createElement("span", { className: "mx-2", textContent: "OR" }, orDiv);
	createElement("hr", { style: hrStyle }, orDiv);
	wrapperDiv.appendChild(orDiv); // Append OR separator

	// --- Card Entry Form ---
	const form = createElement("form", { id: "payment-form" });

	// Billing Details Header
	createElement(
		"h5",
		{ textContent: "Enter billing details", className: "mb-3" },
		form,
	);

	// Billing Input Fields
	const createField = (id: string, placeholder: string, required = true) => {
		const div = createElement("div", { className: "mb-3" });
		createElement(
			"input",
			{
				className: "form-control",
				id: id,
				attributes: {
					type: "text",
					placeholder: placeholder,
					...(required && { required: "true" }),
				},
			},
			div,
		);
		return div;
	};
	form.appendChild(createField("cardName", "Name on Card"));
	form.appendChild(createField("cardAddress", "Street Address"));
	form.appendChild(createField("cardCity", "City"));
	form.appendChild(createField("cardState", "State"));
	// form.appendChild(createField('cardZip', 'Postcode')); // Postal code often comes from Square element

	// Country Select
	const countryDiv = createElement("div", { className: "mb-3" });
	const countrySelect = createElement(
		"select",
		{
			id: "cardCountry",
			className: "form-select",
			attributes: { required: "true" },
		},
		countryDiv,
	);
	const countries = [
		{ value: "AU", text: "Australia" },
		{ value: "NZ", text: "New Zealand" },
		{ value: "US", text: "United States" },
		{ value: "GB", text: "United Kingdom" },
	];
	countries.forEach((c) => {
		createElement(
			"option",
			{
				textContent: c.text,
				attributes: {
					value: c.value,
					...(c.value === "AU" && { selected: "true" }),
				},
			},
			countrySelect,
		);
	});
	form.appendChild(countryDiv);

	// Card Details Header
	createElement(
		"h5",
		{ textContent: "Card Details", className: "mb-3 mt-4" },
		form,
	); // Added margin top

	// Square Card Element Container
	createElement("div", { id: "card-container" }, form); // Square will mount here

	// Save Card Checkbox
	const saveCardDiv = createElement("div", { className: "form-check mt-3" });
	const saveCardInput = createElement(
		"input",
		{
			id: "saveCardCheckbox",
			className: "form-check-input",
			attributes: { type: "checkbox", checked: "true" },
		},
		saveCardDiv,
	);
	createElement(
		"label",
		{
			className: "form-check-label",
			textContent: "Save my card for future purchases",
			attributes: { for: "saveCardCheckbox" },
		},
		saveCardDiv,
	);
	form.appendChild(saveCardDiv);

	// Add form to the main wrapper
	wrapperDiv.appendChild(form);

	// --- Footer and Pay Button ---
	const footer = createElement("div", { id: "footerSection" }); // Wrapper for button
	const buttonFooter = createFooterButton(footer, {
		buttonId: "card-button",
		buttonText: `Pay with credit card (AUD$${
			sessionStorage.getItem("product_price") || "0.00"
		})`,
		disabled: true, // Disabled until Square Card element is ready
	});

	// Associate the submit button with the form explicitly
	const payButton = buttonFooter.querySelector(
		"#card-button",
	) as HTMLButtonElement;
	if (payButton) {
		payButton.setAttribute("form", "payment-form");
	}

	wrapperDiv.appendChild(footer);

	// Payment Status Container
	createElement(
		"div",
		{
			id: "payment-status-container",
			style: { visibility: "hidden", marginTop: "15px" },
		},
		wrapperDiv,
	);

	// Append the whole wrapper to the main container
	container.appendChild(wrapperDiv);

	// --- Initialize Square Elements and Attach Listeners ---
	if (!payments) {
		console.error(
			"Square Payments SDK not initialized before rendering new payment form.",
		);
		messageToast({
			message: "Payment system failed to load. Please refresh.",
			background: "text-bg-danger",
		});
		return;
	}

	// Initialize Card Element
	try {
		card = await payments.card();
		await card.attach("#card-container");
		enableButton("card-button"); // Enable button now that card element is ready
		console.log("Square Card element attached.");
	} catch (error) {
		console.error("Failed to initialize Square Card element:", error);
		messageToast({
			message: "Could not load card entry form. Please refresh.",
			background: "text-bg-danger",
		});
		disableButton("card-button"); // Keep disabled if failed
	}

	// Initialize Wallet Payments (Apple/Google Pay)
	try {
		const paymentRequest = (payments as any).paymentRequest({
			countryCode: "AU",
			currencyCode: "AUD",
			total: {
				amount: sessionStorage.getItem("product_price") || "0.00",
				label: "Total",
			},
		});

		// Apple Pay
		try {
			applePayInstance = await payments.applePay(paymentRequest);
			if (applePayInstance) {
				// Style the button container (SDK might not do this automatically)
				applePayButton.style.display = "inline-block";
				applePayButton.style.webkitAppearance = "-apple-pay-button";
				applePayButton.style.appearance = "-apple-pay-button"; // Standard property
				(applePayButton.style as any).ApplePayButtonType = "plain"; // Use 'plain' or others
				(applePayButton.style as any).ApplePayButtonStyle = "black";
				applePayButton.style.width = "100%"; // Make it full width
				applePayButton.style.height = "44px"; // Standard height
				applePayButton.style.marginTop = "10px";
				applePayButton.lang = "en";

				applePayButton.addEventListener("click", async (event) => {
					event.preventDefault();
					disableButton("card-button"); // Disable card button during wallet payment
					showPaymentProcessingOverlay();
					try {
						await onPayWithNewMethod(
							"applePay",
							applePayInstance,
							undefined,
							undefined,
						);
					} finally {
						hidePaymentProcessingOverlay();
					}
				});
				console.log("Apple Pay Initialized.");
			} else {
				applePayButton.style.display = "none"; // Hide if not available
			}
		} catch (error) {
			console.error("Failed to initialize Apple Pay:", error);
			applePayButton.style.display = "none";
		}

		// Google Pay
		try {
			googlePayInstance = await payments.googlePay(paymentRequest);
			if (googlePayInstance) {
				await googlePayInstance.attach("#google-pay-button", {
					buttonColor: "black",
					buttonSizeMode: "fill",
					buttonType: "long",
				});
				googlePayButton.addEventListener("click", async (event) => {
					event.preventDefault();
					disableButton("card-button");
					showPaymentProcessingOverlay();
					try {
						await onPayWithNewMethod(
							"googlePay",
							googlePayInstance,
							undefined,
							undefined,
						);
					} finally {
						hidePaymentProcessingOverlay();
					}
				});
				console.log("Google Pay Initialized.");
			} else {
				googlePayButton.style.display = "none";
			}
		} catch (error) {
			console.error("Failed to initialize Google Pay:", error);
			googlePayButton.style.display = "none";
		}

		// Hide "OR" separator if both wallet payments failed to initialize
		if (!applePayInstance && !googlePayInstance) {
			orDiv.style.display = "none";
		}
	} catch (error) {
		console.error("Failed to create payment request:", error);
	}

	// Add listener for the main card payment button
	form.addEventListener("submit", async (event) => {
		event.preventDefault();
		if (!card) {
			console.error("Card element not initialized.");
			return;
		}

		// Basic form validation (can be enhanced)
		const billingData = {
			name: (form.querySelector("#cardName") as HTMLInputElement)?.value,
			address: (form.querySelector("#cardAddress") as HTMLInputElement)?.value,
			city: (form.querySelector("#cardCity") as HTMLInputElement)?.value,
			state: (form.querySelector("#cardState") as HTMLInputElement)?.value,
			country: (form.querySelector("#cardCountry") as HTMLSelectElement)?.value,
		};
		const storeCard =
			(form.querySelector("#saveCardCheckbox") as HTMLInputElement)?.checked ??
			false;

		if (
			!billingData.name ||
			!billingData.address ||
			!billingData.city ||
			!billingData.state ||
			!billingData.country
		) {
			messageToast({
				message: "Please fill in all billing details.",
				background: "text-bg-warning",
			});
			return;
		}

		disableButton("card-button"); // Disable on submit
		showPaymentProcessingOverlay();
		try {
			await onPayWithNewMethod("card", card, billingData, storeCard);
		} finally {
			hidePaymentProcessingOverlay();
		}
	});
}

/**
 * Main function to render the payment step.
 * Determines whether to show saved cards or the new payment method form.
 *
 * @param containerId - ID of the container element.
 * @param savedMethods - Array of user's saved payment methods.
 * @param onPaymentSubmit - The callback function to handle the actual payment submission logic (tokenization, API calls).
 * @param darkText - Optional flag for dark text theme.
 */
export async function renderPaymentForm(
	containerId: string,
	savedMethods: SavedPaymentMethod[],
	onPaymentSubmit: (
		methodType: PaymentMethodType,
		paymentMethodInstance: any,
		billingData?: any,
		storeCard?: boolean,
	) => Promise<void>,
	darkText: boolean = false,
): Promise<void> {
	const container = getElementByIdOrThrow(containerId);
	container.innerHTML = ""; // Clear previous content

	//Remove back button at top of page if it exists
	document.getElementById("tee-back-button")?.remove();

	//Clear the status-message-container if it exists
	const statusMessageContainer = document.getElementById(
		"status-message-container",
	);
	if (statusMessageContainer) {
		statusMessageContainer.innerHTML = "";
	}

	// Ensure Square SDK is initialized first
	await initializeSquarePaymentsSDK();
	if (!payments) {
		// Handle case where SDK failed to initialize
		createElement(
			"p",
			{
				textContent: "Error initializing payment system. Please refresh.",
				className: "text-danger text-center",
			},
			container,
		);
		return;
	}

	const showSavedCards = savedMethods && savedMethods.length > 0;

	if (showSavedCards) {
		renderSavedCardForm(
			container,
			savedMethods,
			// Callback for paying with a saved card
			async (methodId) => {
				try {
					// For saved cards, we don't need the instance, just the ID
					await onPaymentSubmit("savedCard", methodId, undefined, undefined);
				} catch (error) {
					// Error handling/message display should be in onPaymentSubmit or the service calling it
					console.error("Error submitting saved card payment:", error);
					enableButton("card-button"); // Re-enable on failure
				}
			},
			// Callback for clicking 'Add New Card'
			() => {
				// Clear the container first, then render the new payment form
				container.innerHTML = "";
				renderNewPaymentMethodForm(container, onPaymentSubmit, darkText);
			},
			darkText,
		);
	} else {
		// No saved cards, render the new payment method form directly
		await renderNewPaymentMethodForm(container, onPaymentSubmit, darkText);
	}
}
